import React, { useEffect, useState, Suspense, lazy } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  matchRoutes,
} from 'react-router-dom';

import Header from './components/Header';
import Footer from './components/Footer';
import Loader from './components/Loader';
import './App.css';
import NotFound from './components/not_found';

// ✅ Lazy loaded pages
const Home = lazy(() => import('./pages/Home'));
const About = lazy(() => import('./pages/About'));
const Privacy = lazy(() => import('./pages/Privacy'));
const Terms = lazy(() => import('./pages/Terms'));
const CookiePolicy = lazy(() => import('./pages/CookiePolicy'));
const CaliforniaPrivacy = lazy(() => import('./pages/CaliforniaPrivacy'));
const CommunityGuidelines = lazy(() => import('./pages/CommunityGuidelines'));
const DatingSafetyTips = lazy(() => import('./pages/DatingSafetyTips'));
const ConsumerHealthDataPrivacy = lazy(() => import('./pages/ConsumerHealthDataPrivacy'));
const SafetyPolicyCenter = lazy(() => import('./pages/SafetyPolicyCenter'));
const Faq = lazy(() => import('./pages/Faq'));
const DownloadPage = lazy(() => import('./components/Download'));

function AppContent() {
  const location = useLocation();
  const [loading, setLoading] = useState(true);

  // ✅ Define all valid routes (excluding "*")
  const routesConfig = [
    { path: '/' },
    { path: '/about' },
    { path: '/privacy' },
    { path: '/terms' },
    { path: '/cookie-policy' },
    { path: '/california-consumer-privacy-act' },
    { path: '/community-guidelines' },
    { path: '/dating-safety-tips' },
    { path: '/consumer-health-data-privacy-policy' },
    { path: '/safety-policy-center' },
    { path: '/faq' },
    { path: '/download' },
    { path: '/instagram' },
    { path: '/facebook' },
    { path: '/linkedin' },
    { path: '/youtube' },
    { path: '/twitter' },
  ];

  // ✅ Detect 404
  const isNotFound = !matchRoutes(routesConfig, location);

  useEffect(() => {
    let timer;
    let isMounted = true;

    window.scrollTo(0, 0);
    setLoading(true);

    const MIN_TIME = 800;
    const MAX_WAIT = 3000;
    const startTime = Date.now();

    const waitForImages = () => {
      const container = document.querySelector('.main-app-content');
      if (!container) return Promise.resolve();

      const images = Array.from(container.querySelectorAll('img')).filter(
        (img) => img.offsetParent !== null
      );

      if (images.length === 0) return Promise.resolve();

      let loaded = 0;

      return new Promise((resolve) => {
        const checkDone = () => {
          loaded++;
          if (loaded === images.length) resolve();
        };

        images.forEach((img) => {
          if (img.complete && img.naturalHeight !== 0) {
            checkDone();
          } else {
            img.onload = checkDone;
            img.onerror = checkDone;
          }
        });
      });
    };

    const finishLoading = async () => {
      try {
        await Promise.race([
          waitForImages(),
          new Promise((res) => setTimeout(res, MAX_WAIT)),
        ]);
      } catch (e) {
        console.warn('Image loading error:', e);
      }

      const elapsed = Date.now() - startTime;
      const remaining = Math.max(MIN_TIME - elapsed, 0);

      timer = setTimeout(() => {
        if (isMounted) setLoading(false);
      }, remaining);
    };

    requestAnimationFrame(() => {
      finishLoading();
    });

    return () => {
      isMounted = false;
      clearTimeout(timer);
    };
  }, [location.pathname]);

  return (
    <>
      {/* ✅ Global Loader */}
      <Loader className={loading} />

      <div className={`main-app-content ${loading ? 'content-hidden' : 'content-visible'}`}>
        {/* ✅ Pass 404 state */}
        <Header isNotFound={isNotFound} />

        <Suspense fallback={<Loader />}>
          <Routes location={location}>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/cookie-policy" element={<CookiePolicy />} />
            <Route path="/california-consumer-privacy-act" element={<CaliforniaPrivacy />} />
            <Route path="/community-guidelines" element={<CommunityGuidelines />} />
            <Route path="/dating-safety-tips" element={<DatingSafetyTips />} />
            <Route path="/consumer-health-data-privacy-policy" element={<ConsumerHealthDataPrivacy />} />
            <Route path="/safety-policy-center" element={<SafetyPolicyCenter />} />
            <Route path="/faq" element={<Faq />} />
            <Route path="/download" element={<DownloadPage />} />

            {/* Social placeholders */}
            <Route path="/instagram" element={<div>Redirecting...</div>} />
            <Route path="/facebook" element={<div>Redirecting...</div>} />
            <Route path="/linkedin" element={<div>Redirecting...</div>} />
            <Route path="/youtube" element={<div>Redirecting...</div>} />
            <Route path="/twitter" element={<div>Redirecting...</div>} />

            {/* ✅ 404 */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>

        <Footer />
      </div>
    </>
  );
}

function App() {
  return (
    <Router>
      <div className="App">
        <AppContent />
      </div>
    </Router>
  );
}

export default App;